/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { of } from 'rxjs';
import { Ballot } from '@src/app/model/ballot';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FieldSet } from '@src/app/shared/form/field-set/field-set.component';
import { FormDateTimeComponent } from '@src/app/shared/form/form-date-time/form-date-time.component';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FormRichTextComponent } from '@src/app/shared/form/form-rich-text/form-rich-text.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { required } from '@src/app/shared/validators';
import { UserService } from '@src/app/modules/user/user.service';
import { QuestionService } from '@src/app/modules/social/question-page/question.service';
import { BallotService } from '@src/app/modules/social/ballot-page/ballot.service';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { CommsTemplateService } from '@src/app/modules/social/comms-template-page/comms-templates.service';
import { NavOption } from '@src/app/model/sys/navOption';
import { CommsTemplate, CommsTemplateType } from '@src/app/model/CommsTemplate';
import { User } from '@src/app/model/user';
import { ChannelPickerComponent } from '@src/app/modules/social/channel-picker/channel-picker.component';
import { uuid } from '@src/app/model/abstract-object';
import { Router } from '@angular/router';
import { BallotPageNavRoute } from '@src/app/routes/app-router';

@Component({
    selector: 'app-ballot-page',
    templateUrl: './ballot-page.component.html',
    styleUrls: ['./ballot-page.component.scss'],
    imports: [FormPageComponent, ChannelPickerComponent]
})
export class BallotPageComponent extends AbstractPageComponent {
  dataSvc = inject(BallotService);
  dialog = inject(MatDialog);
  protected questionSvc = inject(QuestionService);
  private router = inject(Router);
  private currentUserService = inject(CurrentUserService);
  protected userSvc = inject(UserService);


  currentChannelFilter: uuid;

  forumField = FormPicklistComponent.make('Channel where this vote will happen', 'forumId', 'forum',
    { items: this.currentUserService.getForums() }, { validators: [required] }
  );

  newItems : NavOption[] = [
      { name: $localize`New Ballot`, basePath: BallotPageNavRoute.newUrl, id: 'newBallot' }
  ]


  config = new FormConfig({
    navRoute: BallotPageNavRoute,
    title: $localize`Ballot Administration`,
    help: $localize`Ballot of one or more members`,
    fieldSet: new FieldSet({
      fields: [
        FieldMaker.id(),
        FieldMaker.rev(),
        FormTextComponent.make('Title', 'title', { validators: [required] }),
        FormRichTextComponent.make('Details', 'content', { name: 'ballotContent', formColumn: 2 }),
        FormDateTimeComponent.make('Meeting Start', 'opensAt'),
        FormDateTimeComponent.make('Meeting End', 'closesAt'),
        this.forumField,
        FormTextComponent.make('Area', 'areaName'),
        FormTextComponent.make('Core', 'coreName'),
        FormTextComponent.make('Type', 'typeName'),
        FormTextComponent.make('Category', 'categoryName'),        
      ],
      formLayout: [{ cells: [{ width: '33%' }, { width: '66%' }] },
      { cells: [{ colspan: 2 }] }],
    }),
    service: this.dataSvc,
    newOptions: this.newItems,
    mode: 'list',
    objectFactory: () => of(new Ballot()),
    beforeList: this.beforeList.bind(this),
  });

  constructor() {
    const commsTmplSvc = inject(CommsTemplateService);

    super();
    this.currentUserService.getCurrentUser().pipe(takeUntilDestroyed()).subscribe( u => {
        const bp = User.getModelPrivilege(u, 'Ballot');
        this.config.docPath = bp.put
            ? 'omc-community/managers/ballots'
            : 'omc-community/owners-residents/ballots';
        if (bp.post) {
            commsTmplSvc.get(true).subscribe((ts: CommsTemplate[]) => {
                ts.forEach((t, index) => {
                    if (t.type === CommsTemplateType.BALLOT.id) {
                        this.newItems.push(
                            { id: `id${index + 1}`, name: $localize`New ${t.title.substring(0, 30)}`, basePath: BallotPageNavRoute.newUrl, params: { template: t.id } }
                        );
                    }
                })
            })
        }
    })

  }
    beforeList(data: Ballot[], parms) {
        if (parms.map.has('forumId')) {
            this.currentChannelFilter = parms.map.get('forumId').toString();
        } else {
            this.currentChannelFilter = null;
            this.forumField.filter.active = false;
        }
        // sort by date, descending, nulls to the top
        return data.sort((a, b) => (b.opensAt ?? -Infinity) - (a.opensAt ?? -Infinity)); 
    }

    filterByChannel(channelId) {
        //this.currentChannelFilter = channelId;
        if (channelId) {
            this.router.navigate([BallotPageNavRoute.getListRouteName(), { forumId: channelId }])
        } else {
            this.router.navigate([BallotPageNavRoute.getListRouteName()])
        }
    }   
}
